import type {IssuePickerItem} from '@github-ui/item-picker/IssuePicker'
import {type PickerType, RepositoryAndIssuePicker} from '@github-ui/item-picker/RepositoryAndIssuePicker'
import {CommandActionListItem, GlobalCommands} from '@github-ui/ui-commands'
import {ActionList, ActionMenu} from '@primer/react'
import type React from 'react'
import {useCallback, useRef, useState} from 'react'

import {LABELS} from '../../constants/labels'
import {IssueItem, type IssueItemData} from '../IssueItem'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import styles from './RelationshipsSection.module.css'

export type RelationshipData = {
  parent?: IssueItemData | null
}

type RelationshipsSectionProps = {
  sectionHeader: React.ReactElement
  relationships: RelationshipData
  className?: string
}

const RelationshipsSection = ({sectionHeader, relationships, className}: RelationshipsSectionProps) => {
  const {parent} = relationships

  const emptyText = parent ? undefined : LABELS.emptySections.relationships

  return (
    <Section
      id="sidebar-relationships-section"
      sectionHeader={sectionHeader}
      emptyText={emptyText}
      className={className}
    >
      {parent && (
        <ActionList variant="full" className={styles.actionListOverrides}>
          <ActionList.Group>
            <ActionList.GroupHeading as="h4" className={styles.groupHeading}>
              {LABELS.relationNames.parentIssue}
            </ActionList.GroupHeading>
            <IssueItem issue={parent} />
          </ActionList.Group>
        </ActionList>
      )}
    </Section>
  )
}

export type CreateIssueRelationshipsSectionProps = {
  owner: string
  repo: string
  readonly: boolean
  parentIssue?: IssuePickerItem | null
  onParentChange: (parent: IssuePickerItem | null) => void
  className?: string
}

export function CreateIssueRelationshipsSection({
  owner,
  repo,
  readonly,
  parentIssue,
  onParentChange,
  className,
}: CreateIssueRelationshipsSectionProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [pickerType, setPickerType] = useState<PickerType>(null)
  const sectionHeaderRef = useRef<HTMLButtonElement | null>(null)

  const onPickerTypeChange = useCallback((type: PickerType) => {
    setPickerType(type)
  }, [])

  const onParentSelection = useCallback(
    (selectedIssues: IssuePickerItem[]) => {
      const selectedIssue = selectedIssues[0] || null
      onParentChange(selectedIssue)
      setPickerType(null)
    },
    [onParentChange],
  )

  const handleEditParent = useCallback(() => {
    setMenuOpen(false)
    setPickerType('Issue')
  }, [])

  const handleSectionHeaderClick = useCallback(() => {
    setPickerType(null)
    setMenuOpen(o => !o)
  }, [])

  const anchorElement = useCallback(
    (props: unknown) => {
      const {ref} = props as {
        ref: React.MutableRefObject<HTMLButtonElement | null>
      }
      if (ref) {
        ref.current = sectionHeaderRef.current
      }
      return <></>
    },
    [sectionHeaderRef],
  )

  const sectionHeader = readonly ? (
    <ReadonlySectionHeader title={LABELS.sectionTitles.relationships} />
  ) : (
    <>
      <GlobalCommands
        commands={{
          'issue-create:edit-parent': handleEditParent,
        }}
      />
      <SectionHeader
        ref={sectionHeaderRef}
        buttonProps={{
          onClick: handleSectionHeaderClick,
        }}
        readonly={readonly}
        title={LABELS.sectionTitles.relationships}
      />
      <RepositoryAndIssuePicker
        onPickerTypeChange={onPickerTypeChange}
        selectedIssueIds={parentIssue ? [parentIssue.id] : []}
        hiddenIssueIds={[]}
        onIssueSelection={onParentSelection}
        organization={owner}
        defaultRepositoryNameWithOwner={`${owner}/${repo}`}
        pickerType={pickerType}
        anchorElement={anchorElement}
      />
      <ActionMenu open={menuOpen} onOpenChange={setMenuOpen} anchorRef={sectionHeaderRef}>
        <ActionMenu.Overlay width="medium">
          <ActionList>
            <ActionList.Group>
              <CommandActionListItem commandId="issue-create:edit-parent" onSelect={handleEditParent}>
                {parentIssue ? 'Change or remove parent' : 'Add parent'}
              </CommandActionListItem>
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )

  return (
    <RelationshipsSection sectionHeader={sectionHeader} relationships={{parent: parentIssue}} className={className} />
  )
}

try{ RelationshipsSection.displayName ||= 'RelationshipsSection' } catch {}
try{ CreateIssueRelationshipsSection.displayName ||= 'CreateIssueRelationshipsSection' } catch {}